import React from 'react';
import { Search, X } from 'lucide-react';
import Input from './Input';
import IconButton from './IconButton';

export interface SearchFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size' | 'type' | 'className'> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  /** Accessible name of the clear button; the button only shows when this is set. */
  clearLabel?: string;
  'aria-label'?: string;
  id?: string;
  /** `lg` is the large input (52 design px). */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Search input with a leading icon and an optional clear button. Other input props
 * (onKeyDown, role, aria-controls, aria-activedescendant …) go to the input, so it can
 * drive a listbox as a combobox.
 */
export default function SearchField({ value, onChange, placeholder, onClear, clearLabel, 'aria-label': ariaLabel, id, size = 'md', className, ...rest }: SearchFieldProps) {
  return (
    <Input
      {...rest}
      id={id}
      type="search"
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      leading={<Search />}
      containerClassName={className}
      inputSize={size}
      // Hide the browser's native clear X — we already render one via `trailing`.
      className="[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-cancel-button]:hidden"
      trailing={
        clearLabel && value ? (
          <IconButton
            label={clearLabel}
            icon={<X />}
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange('');
              onClear?.();
            }}
          />
        ) : undefined
      }
    />
  );
}
